import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { Modal, Spin } from "antd";
import { ConfirmationModal } from "@/components/confirmationModal";
import { toast } from "react-hot-toast";
import { ConfigurationEditDrawer } from "@/features/assistant/components/ConfigurationEditDrawer";
import { useConfigurationDrawer } from "@/features/assistant/context";
import { IAssistantCart, IWindowCartItem } from "@/features/assistant/types";
import { reconcileFoilCharge } from "@/features/assistant/utils/foilChargeReconciliation";
import { evaluateAllSurgePricesFromMessages } from "@/features/assistant/utils/pricingCalculator";
import { calculatePriceFromMessages } from "@/features/assistant/utils/configurationPriceCalculation";
import { FOIL_SURCHARGE_RESOLVER, MESSAGE_TYPE_ENUM } from "@/features/assistant/constants";
import { useCurrency } from "@/features/product-configurator/hooks/useCurrency";
import {
  deleteSubQuoteProductApi,
  createSubQuoteProductsApi,
  updateSubQuoteProductsApi,
  getSubQuoteProductsApi,
  updateQuoteLineItemApi,
  deleteQuoteLineItemApi,
  updateQuoteApi,
  uploadQuoteImageApi,
  deleteQuoteImageFileApi,
} from "../../../api/dataverse";
import { queryKey } from "../../../constants";
import { IQuoteLineItem, ISubQuoteProductsResponse } from "../../../types";
import { calculatePricingUpdate } from "../../../utils/calculatePricingUpdate";
import { roundToTwoDecimals } from "../../../utils/calculateQuoteTotals";
import { calculateLineItem } from "../../../utils/calculateLineItem";
import { calculateVssLineItem } from "../../../utils/calculateVssLineItem";
import { calculateVssTotals } from "../../../utils/calculateVssTotals";
import { IPriceBreakdownList } from "@/features/assistant/types";
import { diffSubQuoteProducts } from "../../../utils/diffSubQuoteProducts";
import {
  reconstructAssistantMessages,
  transformAToSubQuoteProducts,
} from "../../../utils/transformSubQuoteProducts";
import { QuoteDetailsWindowList } from "../QuoteDetailsWindowList";

interface QuoteImage {
  fileName: string;
  imageUrl: string;
}

interface EditableWindowSectionProps {
  lineItems: IQuoteLineItem[];
  quoteId: string;
  vatPercentage: number;
  customDiscountPercentage: number;
  customDiscountAmount?: number;
  discountType?: 'percentage' | 'amount';
  vssVatPercentage: number;
  quoteImages?: QuoteImage[];
  companyAccount?: { dogma_deliverycharge?: boolean; [key: string]: any };
}

// Must be rendered inside a ConfigurationDrawerProvider
export const EditableWindowSection: React.FC<EditableWindowSectionProps> = ({
  lineItems,
  quoteId,
  vatPercentage,
  customDiscountPercentage,
  customDiscountAmount,
  discountType,
  vssVatPercentage,
  quoteImages = [],
  companyAccount,
}) => {
  const { state, method } = useConfigurationDrawer();
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { formatPrice } = useCurrency();
  const getDeliveryCharge = (subtotal: number) =>
    companyAccount?.dogma_deliverycharge !== true && subtotal < 1000 ? 30 : 0;

  const subProductsResults = useQueries({
    queries: lineItems.map((item) => ({
      queryKey: [queryKey.getSubQuoteProducts, item.quotedetailid],
      queryFn: () => getSubQuoteProductsApi(item.quotedetailid),
      enabled: !!item.quotedetailid,
      staleTime: 2 * 60 * 1000,
    })),
  });

  const isLoadingSubProducts = subProductsResults.some((r) => r.isLoading || r.isFetching);

  const windowCartItems: IWindowCartItem[] = lineItems.map((lineItem, index) => {
    const subProducts = (subProductsResults[index]?.data?.value || []) as any[];
    const messages = reconstructAssistantMessages(subProducts);
    const detailId = lineItem.quotedetailid;

    // Restore window annotation from stored JSON
    let windowAnnotation = { id: "", label: "", x: 0, y: 0, width: 0, height: 0 };
    let windowsAnnotations: any[] = [];
    if (lineItem.dogma_windowannotationjson) {
      try {
        const parsed = JSON.parse(lineItem.dogma_windowannotationjson);
        if (parsed.window) windowAnnotation = parsed.window;
        if (parsed.windows) windowsAnnotations = parsed.windows;
      } catch { }
    }

    // Restore per-line-item images — new quotes use quotedetailid key,
    // old quotes used a sequential index key (fallback).
    const getImageUrl = (prefix: string) =>
      quoteImages.find((img) => img.fileName.endsWith(`${prefix}-${detailId}.png`))?.imageUrl
      ?? quoteImages.find((img) => img.fileName.endsWith(`${prefix}-item-${index + 1}.png`))?.imageUrl;

    const qty = lineItem.quantity || 1;
    return {
      id: detailId,
      windowName: lineItem.dogma_windowname || lineItem.productname || `Window ${index + 1}`,
      messages,
      price: {
        breakdown: [],
        totalPrice: lineItem.baseamount || 0,
        basePrice: lineItem.baseamount || 0,
        surchargePrice: 0,
        totalOptionPrice: 0,
        currency: "GBP",
        vssTotalPrice: (lineItem.dogma_vsssubtotal || 0) / qty,
        vssDiscountAmount: (lineItem.dogma_vssdiscountamount || 0) / qty,
      },
      window: windowAnnotation,
      windows: windowsAnnotations.length > 0 ? windowsAnnotations : undefined,
      quantity: lineItem.quantity || 1,
      timestamp: Date.now(),
      propertyImageUrl: getImageUrl("property"),
      blendedImageUrl: getImageUrl("offline-blend"),
      onlineBlendedImageUrl: getImageUrl("online-blend"),
    };
  });
  const saveConfigurationMutation = useMutation({
    mutationFn: async ({ item: updatedItem, quoteDetailId }: { item: IWindowCartItem; quoteDetailId: string }) => {
      const editingId = quoteDetailId;

      const cachedData = queryClient.getQueryData<ISubQuoteProductsResponse>([
        queryKey.getSubQuoteProducts,
        editingId,
      ]);
      const existingSubs = cachedData?.value || [];
      const newSubProducts = transformAToSubQuoteProducts(updatedItem as any, editingId);

      // ── Sub product diff ──────────────────────────────────────────────────
      const { createPayload: subCreatePayload, updatePayload: subUpdatePayload, deleteIds: subDeleteIds } =
        diffSubQuoteProducts(existingSubs, newSubProducts);

      // ── Pricing recalculation ─────────────────────────────────────────────
      const { lineItemPayload, quotePayload, deliveryCharge, otherLineTaxUpdates } = calculatePricingUpdate({
        updatedItem,
        allLineItems: lineItems,
        updatedLineItemId: editingId,
        vatPercentage,
        customDiscountPercentage,
        customDiscountAmount,
        discountType,
        vssVatPercentage,
        getDeliveryCharge,
      });


      // ── Execute all API calls ─────────────────────────────────────────────
      for (const id of subDeleteIds) { await deleteSubQuoteProductApi(id); }
      if (subCreatePayload.length > 0) { await createSubQuoteProductsApi(subCreatePayload); }
      if (subUpdatePayload.length > 0) { await updateSubQuoteProductsApi(subUpdatePayload); }
      await updateQuoteLineItemApi(editingId, lineItemPayload);
      for (const taxUpdate of otherLineTaxUpdates) {
        await updateQuoteLineItemApi(taxUpdate.quotedetailid, { tax: taxUpdate.tax });
      }
      await updateQuoteApi(quoteId, { ...quotePayload, freightamount: deliveryCharge });

      // ── Upload updated blended images ─────────────────────────────────────
      const uploadImage = async (url: string | undefined, fileName: string) => {
        if (!url) return;
        try {
          const blob = await fetch(url).then((r) => r.blob());
          await uploadQuoteImageApi(blob, quoteId, fileName, blob.type || "image/jpeg");
        } catch (err) {
        }
      };

      // Upload offline blend if it changed
      const originalBlended = quoteImages.find((img) =>
        img.fileName.endsWith(`offline-blend-${editingId}.png`)
      )?.imageUrl;
      const offlineBlendChanged = Boolean(updatedItem.blendedImageUrl && updatedItem.blendedImageUrl !== originalBlended);
      if (offlineBlendChanged) {
        await uploadImage(updatedItem.blendedImageUrl, `offline-blend-${editingId}.png`);

        // When the offline blend changes, delete the stale online blend so the
        // viewer doesn't show an outdated composited image.
        const existingOnlineBlend = quoteImages.find((img) =>
          img.fileName.endsWith(`online-blend-${editingId}.png`)
        );
        if (existingOnlineBlend) {
          try {
            // Extract just the filename portion (strip any leading path segments)
            const onlineFileName = existingOnlineBlend.fileName.split("/").pop() || `online-blend-${editingId}.png`;
            await deleteQuoteImageFileApi(quoteId, onlineFileName);
          } catch (err) {
          }
        }
      }

      // Upload online blend if it changed
      const originalOnline = quoteImages.find((img) =>
        img.fileName.endsWith(`online-blend-${editingId}.png`)
      )?.imageUrl;
      if (updatedItem.onlineBlendedImageUrl && updatedItem.onlineBlendedImageUrl !== originalOnline) {
        await uploadImage(updatedItem.onlineBlendedImageUrl, `online-blend-${editingId}.png`);
      }
    },
    onSuccess: (_data, vars, _ctx) => {
      const editingId = vars.quoteDetailId;
      queryClient.invalidateQueries({ queryKey: [queryKey.getSubQuoteProducts, editingId] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteLineItems] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteDetail] });
      // Blob storage serves images with a 1-year cache header, so the browser
      // won't reload them even after upload. Bust the cache by appending a
      // timestamp to every imageUrl in the cached query data.
      // Do NOT invalidate/refetch — the refetch would overwrite the timestamped
      // URLs with the original ones, causing the browser to serve the old image.
      const t = Date.now();
      queryClient.setQueryData([queryKey.getQuoteImage, quoteId], (old: any) => {
        if (!old?.images) return old;
        return {
          ...old,
          images: old.images.map((img: any) => ({
            ...img,
            imageUrl: img.imageUrl
              ? `${img.imageUrl.split('?')[0]}?t=${t}`
              : img.imageUrl,
          })),
        };
      });
      toast.success("Configuration updated successfully");
    },
  });

  // ── Quantity change ───────────────────────────────────────────────────────
  const updateQuantityMutation = useMutation({
    mutationFn: async ({ itemId, newQuantity }: { itemId: string; newQuantity: number }) => {
      const lineItem = lineItems.find((li) => li.quotedetailid === itemId);
      if (!lineItem) throw new Error("Line item not found");

      const qty = lineItem.quantity || 1;
      // priceperunit is explicitly per-unit and D365 never overrides it.
      // baseamount is set by D365 as priceperunit × quantity so must NOT be used as per-unit.
      const perUnitPrice = lineItem.priceperunit || 0;
      const itemPrice: IPriceBreakdownList = {
        totalPrice: perUnitPrice,
        breakdown: [],
        basePrice: perUnitPrice,
        surchargePrice: 0,
        totalOptionPrice: 0,
        currency: "GBP",
        vssTotalPrice: roundToTwoDecimals((lineItem.dogma_vsssubtotal || 0) / qty),
        totalVssDiscountAmount: roundToTwoDecimals((lineItem.dogma_vssdiscountamount || 0) / qty),
      };

      // Quote-level normal totals — use priceperunit (per-unit) for all items
      const allItemPrices = lineItems.map((li) => ({
        totalPrice: li.priceperunit || 0,
        quantity: li.quotedetailid === itemId ? newQuantity : (li.quantity || 1),
      }));
      const subtotal = roundToTwoDecimals(allItemPrices.reduce((sum, i) => sum + roundToTwoDecimals(i.totalPrice * i.quantity), 0));

      const lineItemCalc = calculateLineItem({
        itemPrice,
        quantity: newQuantity,
        customDiscountPercentage,
        customDiscountAmount,
        discountType,
        subtotal,
        vatPercentage,
      });

      const vssLineItemCalc = calculateVssLineItem({
        itemPrice,
        quantity: newQuantity,
        vatPercentage: vssVatPercentage,
      });
      const totalDiscountAmount = discountType === 'amount' && customDiscountAmount !== undefined
        ? roundToTwoDecimals(customDiscountAmount)
        : roundToTwoDecimals(subtotal * (customDiscountPercentage / 100));
      const subtotalAfterDiscount = roundToTwoDecimals(subtotal - totalDiscountAmount);
      const deliveryCharge = getDeliveryCharge(subtotal);
      const vatBase = roundToTwoDecimals(subtotalAfterDiscount + deliveryCharge);
      const vat = roundToTwoDecimals(vatBase * (vatPercentage / 100));
      const totalAmount = roundToTwoDecimals(vatBase + vat);

      // Spread the delivery VAT proportionally across every line's `tax` so the
      // Dataverse totaltax rollup matches the delivery-inclusive totaltax above.
      const deliveryVatTotal = roundToTwoDecimals(deliveryCharge * (vatPercentage / 100));
      const deliveryVatShareFor = (extendedAmount: number): number =>
        subtotalAfterDiscount > 0
          ? roundToTwoDecimals((extendedAmount / subtotalAfterDiscount) * deliveryVatTotal)
          : 0;
      const otherLineTaxUpdates = lineItems
        .filter((li) => li.quotedetailid !== itemId)
        .map((li) => {
          const calc = calculateLineItem({
            itemPrice: { totalPrice: li.priceperunit || 0 } as IPriceBreakdownList,
            quantity: li.quantity || 1,
            customDiscountPercentage,
            customDiscountAmount,
            discountType,
            subtotal,
            vatPercentage,
          });
          return {
            quotedetailid: li.quotedetailid,
            tax: roundToTwoDecimals(calc.taxAmount + deliveryVatShareFor(calc.extendedAmount)),
          };
        });

      // Quote-level VSS totals
      const allVssItems = lineItems.map((li) => {
        if (li.quotedetailid === itemId) {
          return { price: itemPrice, quantity: newQuantity };
        }
        const liQty = li.quantity || 1;
        return {
          price: {
            vssTotalPrice: roundToTwoDecimals((li.dogma_vsssubtotal || 0) / liQty),
            totalVssDiscountAmount: roundToTwoDecimals((li.dogma_vssdiscountamount || 0) / liQty),
          } as IPriceBreakdownList,
          quantity: liQty,
        };
      });
      const vssTotals = calculateVssTotals({ cartItems: allVssItems, vatPercentage: vssVatPercentage, deliveryCharge });

      await updateQuoteLineItemApi(itemId, {
        quantity: newQuantity,
        priceperunit: lineItemCalc.itemTotalPrice,
        extendedamount: lineItemCalc.extendedAmount,
        tax: roundToTwoDecimals(lineItemCalc.taxAmount + deliveryVatShareFor(lineItemCalc.extendedAmount)),
        manualdiscountamount: lineItemCalc.totalDiscountAmount,
        dogma_vsssubtotal: vssLineItemCalc.vssExtendedAmount,
        dogma_vssdiscountamount: vssLineItemCalc.vssDiscountAmount,
        dogma_vssvatamount: vssLineItemCalc.vssVatAmount,
        dogma_vsstotalamount: vssLineItemCalc.vssLineTotal,
      });
      for (const taxUpdate of otherLineTaxUpdates) {
        await updateQuoteLineItemApi(taxUpdate.quotedetailid, { tax: taxUpdate.tax });
      }
      await updateQuoteApi(quoteId, {
        totaldiscountamount: totalDiscountAmount,
        totaltax: vat,
        totalamount: totalAmount,
        freightamount: deliveryCharge,
        dogma_vsssubtotal: vssTotals.vssSubtotal,
        dogma_vssdiscountamount: vssTotals.vssDiscountAmount,
        dogma_vssvatamount: vssTotals.vssVat,
        dogma_vsstotalamount: vssTotals.vssTotalAmount,
        dogma_vssvatpercentage: vssVatPercentage,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteLineItems] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteDetail] });
      toast.success("Quantity updated");
    },
  });

  // ── Delete line item ──────────────────────────────────────────────────────
  const deleteLineItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      // Delete all sub products first
      const cachedData = queryClient.getQueryData<ISubQuoteProductsResponse>([
        queryKey.getSubQuoteProducts,
        itemId,
      ]);
      const subProducts = cachedData?.value || [];
      for (const sp of subProducts) {
        await deleteSubQuoteProductApi(sp.dogma_subquoteproductid);
      }
      // Delete the line item
      await deleteQuoteLineItemApi(itemId);

      // Foil charge reconciliation — if the deleted window owned the flat foil
      // charge, find the next eligible window and assign it there.
      const FOIL_RESOLVER = FOIL_SURCHARGE_RESOLVER;
      const deletedCartItem = windowCartItems.find(w => w.id === itemId);
      const deletedHadFoil = deletedCartItem?.messages.some(m =>
        m.type === MESSAGE_TYPE_ENUM.priceSurge &&
        (m.surgeItems ?? []).some((s: any) => s.resolverValue === FOIL_RESOLVER),
      );

      if (deletedHadFoil) {
        const { questionFlow, marginPercentage } = state;
        const remainingCartItems = windowCartItems.filter(w => w.id !== itemId);
        for (const candidate of remainingCartItems) {
          const candidateBase = candidate.messages.filter(m => m.type !== MESSAGE_TYPE_ENUM.priceSurge);
          const candidateSurges = evaluateAllSurgePricesFromMessages(
            candidateBase, questionFlow, formatPrice, marginPercentage,
          );
          const hasFoil = candidateSurges.some(
            m => (m.surgeItems ?? []).some((s: any) => s.resolverValue === FOIL_RESOLVER),
          );
          if (hasFoil) {
            const reassignedMessages = [...candidateBase, ...candidateSurges];
            const reassignedItem: IWindowCartItem = {
              ...candidate,
              messages: reassignedMessages,
              price: calculatePriceFromMessages(reassignedMessages),
            };
            // Save the foil-reassigned window to CRM
            await saveConfigurationMutation.mutateAsync({
              item: reassignedItem,
              quoteDetailId: reassignedItem.id,
            });
            break;
          }
        }
      }

      // Recalculate quote totals with remaining line items
      const remainingItems = lineItems.filter((li) => li.quotedetailid !== itemId);
      const subtotal = roundToTwoDecimals(remainingItems.reduce((sum, li) => sum + roundToTwoDecimals((li.baseamount || 0) * (li.quantity || 1)), 0));
      const totalDiscountAmount = discountType === 'amount' && customDiscountAmount !== undefined
        ? roundToTwoDecimals(customDiscountAmount)
        : roundToTwoDecimals(subtotal * (customDiscountPercentage / 100));
      const subtotalAfterDiscount = roundToTwoDecimals(subtotal - totalDiscountAmount);
      const deliveryCharge = getDeliveryCharge(subtotal);
      const vatBase = roundToTwoDecimals(subtotalAfterDiscount + deliveryCharge);
      const vat = roundToTwoDecimals(vatBase * (vatPercentage / 100));
      const totalAmount = roundToTwoDecimals(vatBase + vat);

      // Re-spread the delivery VAT across the remaining lines' `tax` so the
      // Dataverse totaltax rollup matches the delivery-inclusive totaltax above.
      const deliveryVatTotal = roundToTwoDecimals(deliveryCharge * (vatPercentage / 100));
      const deliveryVatShareFor = (extendedAmount: number): number =>
        subtotalAfterDiscount > 0
          ? roundToTwoDecimals((extendedAmount / subtotalAfterDiscount) * deliveryVatTotal)
          : 0;
      const otherLineTaxUpdates = remainingItems.map((li) => {
        const calc = calculateLineItem({
          itemPrice: { totalPrice: li.priceperunit || 0 } as IPriceBreakdownList,
          quantity: li.quantity || 1,
          customDiscountPercentage,
          customDiscountAmount,
          discountType,
          subtotal,
          vatPercentage,
        });
        return {
          quotedetailid: li.quotedetailid,
          tax: roundToTwoDecimals(calc.taxAmount + deliveryVatShareFor(calc.extendedAmount)),
        };
      });

      // Quote-level VSS totals across remaining line items — same pattern as
      // updateQuantityMutation/calculatePricingUpdate. Without this, the VSS
      // fields (dogma_vsstotalamount etc.) — what the Quote Details page
      // actually displays — were left stale after deleting a window.
      const allVssItems = remainingItems.map((li) => {
        const qty = li.quantity || 1;
        return {
          price: {
            vssTotalPrice: roundToTwoDecimals((li.dogma_vsssubtotal || 0) / qty),
            totalVssDiscountAmount: roundToTwoDecimals((li.dogma_vssdiscountamount || 0) / qty),
          } as IPriceBreakdownList,
          quantity: qty,
        };
      });
      const vssTotals = calculateVssTotals({ cartItems: allVssItems, vatPercentage: vssVatPercentage, deliveryCharge });

      for (const taxUpdate of otherLineTaxUpdates) {
        await updateQuoteLineItemApi(taxUpdate.quotedetailid, { tax: taxUpdate.tax });
      }
      await updateQuoteApi(quoteId, {
        totaldiscountamount: totalDiscountAmount,
        totaltax: vat,
        totalamount: totalAmount,
        freightamount: deliveryCharge,
        dogma_vsssubtotal: vssTotals.vssSubtotal,
        dogma_vssdiscountamount: vssTotals.vssDiscountAmount,
        dogma_vssvatamount: vssTotals.vssVat,
        dogma_vsstotalamount: vssTotals.vssTotalAmount,
        dogma_vssvatpercentage: vssVatPercentage,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteLineItems] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteDetail] });
      toast.success("Window removed");
      setDeleteItemId(null);
    },
  });

  const handleEditConfiguration = (
    itemId: string,
    item: IWindowCartItem,
    questionId: string
  ) => {
    const cart: IAssistantCart = { customer: {}, windowConfiguration: windowCartItems };
    method.openDrawer(itemId, item, questionId, cart);
  };

  const handleSaveConfiguration = async (updatedItem: IWindowCartItem) => {
    const editingId = state.editingItemId!;
    // Save the primary (edited) window
    await saveConfigurationMutation.mutateAsync({ item: updatedItem, quoteDetailId: editingId });

    // Reconcile foil charge across all windows and save any that changed
    const { questionFlow, marginPercentage } = state;
    const afterPrimary = windowCartItems.map(w => w.id === updatedItem.id ? updatedItem : w);
    const reconciled = reconcileFoilCharge(afterPrimary, questionFlow, formatPrice, marginPercentage);

    for (const reconciledItem of reconciled) {
      if (reconciledItem.id === editingId) continue; // already saved above
      const original = windowCartItems.find(w => w.id === reconciledItem.id);
      if (!original) continue;
      const originalHasFoil = original.messages.some(m =>
        m.type === 'price-surge' &&
        (m.surgeItems ?? []).some((s: any) => s.resolverValue === FOIL_SURCHARGE_RESOLVER),
      );
      const reconciledHasFoil = reconciledItem.messages.some(m =>
        m.type === 'price-surge' &&
        (m.surgeItems ?? []).some((s: any) => s.resolverValue === FOIL_SURCHARGE_RESOLVER),
      );
      if (originalHasFoil !== reconciledHasFoil) {
        // Only update this window's sub-products and line item price.
        // Do NOT call updateQuoteApi again — the primary save already updated totals
        // and a second call causes a CRM concurrency error.
        const targetId = reconciledItem.id;
        const cachedSubs = queryClient.getQueryData<ISubQuoteProductsResponse>([
          queryKey.getSubQuoteProducts, targetId,
        ]);
        const existingSubs = cachedSubs?.value || [];
        const newSubProducts = transformAToSubQuoteProducts(reconciledItem as any, targetId);
        const { createPayload, updatePayload, deleteIds } = diffSubQuoteProducts(existingSubs, newSubProducts);
        for (const id of deleteIds) { await deleteSubQuoteProductApi(id); }
        if (createPayload.length > 0) { await createSubQuoteProductsApi(createPayload); }
        if (updatePayload.length > 0) { await updateSubQuoteProductsApi(updatePayload); }
        const { lineItemPayload } = calculatePricingUpdate({
          updatedItem: reconciledItem,
          allLineItems: lineItems,
          updatedLineItemId: targetId,
          vatPercentage,
          customDiscountPercentage,
          vssVatPercentage,
        });
        await updateQuoteLineItemApi(targetId, lineItemPayload);
        queryClient.invalidateQueries({ queryKey: [queryKey.getSubQuoteProducts, targetId] });
      }
    }
  };

  const isUpdating = updateQuantityMutation.isPending || deleteLineItemMutation.isPending;
  const isSaving = saveConfigurationMutation.isPending;
  const isProcessing = isUpdating || isSaving;

  const processingMessage = deleteLineItemMutation.isPending
    ? "Removing window…"
    : isSaving
      ? "Updating Quote"
      : "Updating…";

  const processingSubtitle = deleteLineItemMutation.isPending
    ? "Removing the window from your quote"
    : isSaving
      ? "Saving your changes to the quote"
      : "Updating quote details";

  return (
    <>
      <Modal
        open={isProcessing}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={320}
      >
        <div className="flex flex-col items-center py-6 gap-4">
          <Spin size="large" />
          <div className="text-center">
            <p className="text-gray-900 font-semibold text-base">{processingMessage}</p>
            <p className="text-gray-500 text-sm mt-1">{processingSubtitle}</p>
          </div>
        </div>
      </Modal>
      <QuoteDetailsWindowList
        windowConfigurations={windowCartItems}
        onEditConfiguration={handleEditConfiguration}
        onUpdateQuantity={(itemId, qty) => updateQuantityMutation.mutate({ itemId, newQuantity: qty })}
        onDelete={(itemId) => setDeleteItemId(itemId)}
        disableAllActions={isProcessing}
        isLoadingConfigurations={isLoadingSubProducts}
      />
      <ConfigurationEditDrawer
        onSave={handleSaveConfiguration}
        isSaving={saveConfigurationMutation.isPending}
      />
      <ConfirmationModal
        open={!!deleteItemId}
        content="Are you sure you want to remove this window from the quote? This action cannot be undone."
        cancelBtnText="Cancel"
        okBtnText="Remove"
        loading={deleteLineItemMutation.isPending}
        onClose={() => setDeleteItemId(null)}
        onConfirm={() => deleteItemId && deleteLineItemMutation.mutate(deleteItemId)}
      />
    </>
  );
};
